import PropTypes from 'prop-types'
import React, { Component } from 'react'

const ALIGN = ['start', 'center', 'end']
const LABEL_POSITION = ['top', 'bottom', 'left', 'right']
const RESPECT_DIRECTION = ['width', 'height']
const BORDER_STYLES = ['dotted', 'dashed', 'solid', 'none']
const WEIGHT = ['normal', 'bold', 'bolder']
const STYLES = ['normal', 'italic', 'oblique']
const color = PropTypes.color

export const theme = {
  borderColor: color,
  borderWidth: PropTypes.string,
  borderStyle: PropTypes.oneOf(BORDER_STYLES),
  labelColor: color,
  labelWeight: PropTypes.oneOf(WEIGHT),
  labelStyle: PropTypes.oneOf(STYLES)
}

/**
 * Image component
 */
export default class Image extends Component {
  static propTypes = {
    /**
     * Source of image to be displayed. Can be a hardwired link, or bound to a dynamic data source.
     */
    src: PropTypes.string.isRequired,

    /**
     * Alternative text if image N/A.
     */
    alt: PropTypes.string.isRequired,

    /**
     * Adds sizing according width or height
     */
    respectSize: PropTypes.oneOf(RESPECT_DIRECTION),

    /**
     * Radius for the rounded corners in %
     */
    roundingRadius: PropTypes.number,

    /**
     * Position an image horizontally to the start, end, or center of the container (default is 'start').
     */
    imageAlign: PropTypes.oneOf(ALIGN),

    /**
     * Label to be displayed. Can be styled with CSS.
     */
    label: PropTypes.string,

    /**
     * Label position
     */
    labelPosition: PropTypes.oneOf(LABEL_POSITION),

    /**
     * Label alignment (default is 'right')
     */
    labelAlign: PropTypes.oneOf(ALIGN),

    /**
     * Vertical position for the image to start, bottom or center of the container (default is 'start').
     */

    verticalAlign: PropTypes.oneOf(ALIGN),

    /**
     * Determines if the image has border
     */
    border: PropTypes.bool,

    /**
     * Adjust the colors, borders, and alignment
     */
    styling: PropTypes.shape(theme)
  }

  static defaultProps = {
    labelPosition: 'bottom',
    labelAlign: 'right',
    imageAlign: 'start',
    verticalAlign: 'start',
    border: false
  }

  getProperAlignment(propertyAlignment, direction) {
    const {
      imageAlign,
      verticalAlign,
      labelPosition,
      respectSize = {}
    } = this.props;

    if (respectSize === 'width' && direction === 'column-reverse' && labelPosition === 'top' && propertyAlignment === 'vertical') {
      return verticalAlign === 'end' ? 'start' : 'end';
    }
    if (respectSize === 'height' && direction === 'row-reverse' && labelPosition === 'left' && propertyAlignment === 'horizontal') {
      return imageAlign === 'end' ? 'start' : 'end';
    }

    return propertyAlignment === 'vertical' ? verticalAlign : imageAlign;
  }

  render () {
    const {
      src,
      alt,
      label,
      border,
      imageAlign,
      labelAlign,
      verticalAlign,
      labelPosition,
      respectSize,
      roundingRadius,
      style,
      styling: {
        borderWidth,
        borderStyle,
        borderColor,
        labelColor,
        labelWeight,
        labelStyle
      } = {} } = this.props

    const direction = respectSize === 'width'
    ? (labelPosition === `top` && `column-reverse`) || (labelPosition === `bottom` && `column`) || (labelPosition === `left` && `row-reverse`) || (labelPosition === `right` && `row`)
    : (labelPosition === `left` && `row-reverse`) || (labelPosition === `right` && `row`) || (labelPosition === `top` && `column-reverse`) || (labelPosition === `bottom` && `column`);

    return (
      <div className={`image-container`}>
        <div className={`image`}>
          <img
            src={src || ''}
            alt={alt}
            style={style}
          />
        </div>
        {
          label &&
          <div className={`label`}>
            <span>
              {label}
            </span>
          </div>
        }
        <style jsx>
          {`
            .image-container {
              width: 100%;
              height: 100%;
              position: relative;
              display: flex;
              flex-direction: ${direction};
              align-items: ${label && respectSize === 'height' ? (imageAlign === `center` ? `center` : `flex-${this.getProperAlignment('horizontal', direction)}`) : (verticalAlign === `center` ? `center` : `flex-${this.getProperAlignment('vertical', direction)}`)};
              justify-content: ${label && respectSize === 'width' ? (verticalAlign === `center` ? `center` : `flex-${this.getProperAlignment('vertical', direction)}`) : (imageAlign === `center` ? `center` : `flex-${this.getProperAlignment('horizontal', direction)}`)};
              }
            .image {
              display: flex;
              width: ${label && respectSize === `height` && 'auto' || `100%`};
              height: ${label && respectSize === `width` && 'auto' || `100%`};
              align-items: ${verticalAlign === `center` ? `center` : `flex-${verticalAlign}`};
              justify-content: ${imageAlign === `center` ? `center` : `flex-${imageAlign}`};
            }
            img {
              width: ${(respectSize && respectSize === 'width' && `100%`) || 'auto'};
              height: ${(respectSize && respectSize === 'height' && `100%`) || 'auto'};
              border-radius: ${(!!roundingRadius && roundingRadius) || 0}%;
              border-width: ${(!!border && !!borderWidth && borderWidth) || 0};
              border-style: ${(!!border && !!borderStyle && borderStyle) || 'none'};
              border-color: ${(!!border && !!borderColor && borderColor) || 'transparent'};
            }
            .label {
              display: flex;
              width: ${(direction === 'column' || direction === 'column-reverse') ? `inherit` : `auto`};
              height: ${(direction === 'row' || direction === 'row-reverse') ? `inherit` : `auto`};
              align-items: ${labelAlign === `center` ? `center` : `flex-${labelAlign}`};
              justify-content: ${labelAlign === `center` ? `center` : `flex-${labelAlign}`};
            }
            .label span {
              color: ${(!!labelColor && labelColor) || 'inherit'};
              font-weight: ${(!!labelWeight && labelWeight) || 'normal'};
              font-style: ${(!!labelStyle && labelStyle) || 'normal'};
            }
          `}
        </style>
      </div>
    )
  }
}
